import { AnimatePresence, motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { useEffect, useState, useRef } from 'react'
import { GiHamburgerMenu } from 'react-icons/gi'
import { RxCross1 } from 'react-icons/rx'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import MobileNavItem from './MobileNavItem'
import Link from 'next/link'
import companylogo from '../public/Home/lg.png' 
import Image from 'next/image'
import { usePathname } from 'next/navigation'

// Hook to track container dimensions for circular clip-path animation
const useDimensions = (ref) => {
    const dimensions = useRef({ width: 0, height: 0 })

    useEffect(() => {
        if (ref.current) {
            dimensions.current.width = ref.current.offsetWidth
            dimensions.current.height = ref.current.offsetHeight
        }
    }, [ref])

    useEffect(() => {
        const updateDimensions = () => {
            if (ref.current) {
                dimensions.current.width = ref.current.offsetWidth
                dimensions.current.height = ref.current.offsetHeight
            }
        }

        // Update dimensions on resize
        window.addEventListener('resize', updateDimensions)
        
        // Update dimensions when menu opens/closes
        updateDimensions()
        
        return () => {
            window.removeEventListener('resize', updateDimensions)
        }
    }, [ref])

    return dimensions.current
}

// Variants for sidebar background with circular clip-path animation
const sidebarVariants = {
    open: (height = 1000) => ({
        clipPath: `circle(${height * 2 + 200}px at 40px 40px)`,
        transition: {
            type: "spring",
            stiffness: 20,
            restDelta: 2,
        },
    }),
    closed: {
        clipPath: "circle(30px at 40px 40px)",
        transition: {
            delay: 0.2,
            type: "spring",
            stiffness: 400,
            damping: 40,
        },
    },
}

// Variants for navigation container with stagger children
const navVariants = {
    open: {
        transition: { staggerChildren: 0.07, delayChildren: 0.2 },
    },
    closed: {
        transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
}

// Variants for individual menu items
const itemVariants = {
    open: {
        y: 0,
        opacity: 1,
        transition: {
            y: { stiffness: 1000, velocity: -100 },
        },
    },
    closed: {
        y: 50,
        opacity: 0,
        transition: {
            y: { stiffness: 1000 },
        },
    },
}

const MobileNav = ({ navbarData, overLight = false }) => {
	const pathName = usePathname()
    const [isMenuShown, setIsMenuShown] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [mounted, setMounted] = useState(false)
    const [expandedSections, setExpandedSections] = useState({})
    const containerRef = useRef(null)
    const dimensions = useDimensions(containerRef)
    const height = dimensions.height

    // Portal target (document.body) is only available on the client.
    useEffect(() => {
        setMounted(true)
    }, [])

    // Check if the viewport is mobile on mount and when resized
    useEffect(() => {
        const checkMobile = () => {
            // Adjust the breakpoint value if necessary
            setIsMobile(window.innerWidth < 1080)
        }

        // Do an initial check
        checkMobile()

        window.addEventListener('resize', checkMobile)
        return () => {
            window.removeEventListener('resize', checkMobile)
        }
    }, [])

    // Toggle body overflow only in mobile view
    useEffect(() => {
        if (isMobile) {
            document.body.style.overflow = isMenuShown ? 'hidden' : 'auto'
        } else {
            // Always enable scrolling in desktop view
            document.body.style.overflow = 'auto'
        }

        // Clean up on unmount
        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [isMenuShown, isMobile])

    // Update dimensions when menu opens
    useEffect(() => {
        if (isMenuShown && containerRef.current) {
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                if (containerRef.current) {
                    dimensions.width = containerRef.current.offsetWidth
                    dimensions.height = containerRef.current.offsetHeight
                }
            }, 50)
            return () => clearTimeout(timer)
        }
    }, [isMenuShown, dimensions])

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }))
    }

    return (
        <div className='flex flex-col nav-md:hidden'>
            
            <button
                type="button"
                aria-label="Open navigation menu"
                className={`cursor-pointer rounded-lg border p-2 text-2xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#058BF0]/50 focus-visible:ring-offset-2 ${
                    overLight
                        ? 'border-[#0a1b3a]/10 bg-[#0a1b3a]/[0.04] text-[#0a1b3a] hover:bg-[#0a1b3a]/[0.08]'
                        : 'border-white/15 bg-white/[0.08] text-white hover:bg-white/[0.15]'
                }`}
                onClick={() => setIsMenuShown(true)}>

                <GiHamburgerMenu />
            </button>

            {mounted &&
                createPortal(
                    <AnimatePresence>
                        {isMenuShown && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className='fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm'
                        onClick={() => setIsMenuShown(false)}>
                        <motion.nav
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'tween', duration: 0.3 }}
                            custom={height}
                            ref={containerRef}
                            className='ml-auto flex h-full w-[80%] flex-col overflow-auto bg-gradient-to-b from-white to-gray-50 px-6 py-6 sm:w-[35%] sm:min-w-[600px] md:min-w-[400px] shadow-2xl relative'
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Circular clip-path background animation */}
                            <motion.div 
                                className='absolute top-0 left-0 bottom-0 w-full bg-gradient-to-b from-white to-gray-50'
                                initial="closed"
                                animate={isMenuShown ? "open" : "closed"}
                                custom={height}
                                variants={sidebarVariants}
                            />
                            
                            <div className='pb-6 flex justify-between items-center border-gray-200 relative z-10'>
                                <Link href='/' onClick={() => setIsMenuShown(false)}>
                                    <Image
                                        alt='company logo'
                                        loading='eager'
                                        width={400}
                                        height={400}
                                        className='w-[180px] brightness-110 filter md:w-[200px]'
                                        src='/Cloudastra-Logo-big.png'
                                    />
                                </Link>
                                <button
                                    type="button"
                                    aria-label="Close navigation menu"
                                    className='cursor-pointer rounded-full bg-gray-100 p-2 transition-all duration-200 hover:bg-gray-200 hover:scale-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#058BF0]/50 focus-visible:ring-offset-2'
                                    onClick={() => setIsMenuShown(false)}>
                                    <RxCross1 className='text-gray-800' />
                                </button>
                            </div>
                            

                            <motion.div
                                initial="closed"
                                animate={isMenuShown ? "open" : "closed"}
                                variants={navVariants}
                                className='flex flex-col gap-2 relative z-10'>
                                {navbarData.map((data) => (
                                    <motion.div
                                        key={'mobileNav' + data.id}
                                        variants={itemVariants}>
                                        <MobileNavItem 
                                            data={data} 
                                            expandedSections={expandedSections}
                                            toggleSection={toggleSection}
                                            closeMenu={() => setIsMenuShown(false)}
                                        />
                                    </motion.div>
                                ))}
                               <motion.div 
  variants={itemVariants}
  className='mt-6 pt-4 border-t border-gray-200'
>

  {pathName === '/contact' ? (
    <span className='block w-full py-3 px-4 text-center cursor-default rounded-lg text-base font-bold text-white shadow-lg bg-[#058af0]'>
      Contact Us
    </span>
  ) : (
    <Link
      href='/contact?ref=navbar&utm_source=website&utm_medium=cta&utm_campaign=homepage&utm_content=home__mobile_navbar__contact_us'
      onClick={() => setIsMenuShown(false)}
      className='block w-full py-3 px-4 text-center cursor-pointer rounded-lg text-base font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:bg-[#0470C4] bg-[#058af0] transform hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#058BF0]/50 focus-visible:ring-offset-2'
    >
      Contact Us
    </Link>
  )}
</motion.div>

                                
                            </motion.div>
                        </motion.nav>
                    </motion.div>
                        )}
                    </AnimatePresence>,
                    document.body,
                )}
        </div>
    )
}

export default MobileNav
